// components/Admin/HL7MessageAuditLog.jsx
// Read-only audit log of inbound (ORM) and outbound (ORU) HL7 messages.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, CircularProgress, Chip, TextField, MenuItem,
  TablePagination, Alert, Snackbar,
} from '@mui/material';
import { Visibility as ViewIcon, Replay as RetryIcon, Close as CloseIcon } from '@mui/icons-material';
import { hl7Service } from '../../services/hl7';
import api from '../../services/api';
import { useThemeMode } from '../../contexts/ThemeModeContext';

const STATUS_COLOR = {
  received: 'default', processed: 'success', sent: 'info',
  acked: 'success', failed: 'error',
};

export default function HL7MessageAuditLog() {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [direction, setDirection] = useState('');
  const [type, setType] = useState('');
  const [detail, setDetail] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await hl7Service.listMessages({
        page: page + 1,
        limit: rowsPerPage,
        ...(direction ? { direction } : {}),
        ...(type ? { type } : {}),
      });
      setMessages(data.messages || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load HL7 messages:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, direction, type]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const openDetail = async (msg) => {
    try {
      const { data } = await hl7Service.getMessageById(msg._id);
      setDetail(data);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load message detail', severity: 'error' });
    }
  };

  const handleRetry = async (msg) => {
    try {
      await api.post(`/hl7/messages/${msg._id}/retry`);
      setSnackbar({ open: true, message: 'Retry queued', severity: 'success' });
      fetchMessages();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Retry failed', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>📨 HL7 Message Audit Log</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField select size="small" label="Direction" value={direction}
          onChange={e => { setPage(0); setDirection(e.target.value); }} sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="inbound">Inbound (ORM)</MenuItem>
          <MenuItem value="outbound">Outbound (ORU)</MenuItem>
        </TextField>
        <TextField size="small" label="Message Type" placeholder="e.g. ORU" value={type}
          onChange={e => { setPage(0); setType(e.target.value); }} sx={{ minWidth: 160 }} />
      </Box>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Direction</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Facility</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Retries</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : messages.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No HL7 messages yet.</Typography>
              </TableCell></TableRow>
            ) : messages.map(m => (
              <TableRow key={m._id} hover>
                <TableCell>{new Date(m.createdAt).toLocaleString()}</TableCell>
                <TableCell>
                  <Chip label={m.direction} size="small" color={m.direction === 'inbound' ? 'primary' : 'secondary'} variant="outlined" />
                </TableCell>
                <TableCell>{m.messageType}</TableCell>
                <TableCell>{m.relatedFacility?.facilityName || '—'}</TableCell>
                <TableCell>{m.relatedOrderId?.accessionNumber || '—'}</TableCell>
                <TableCell>
                  <Chip label={m.status} size="small" color={STATUS_COLOR[m.status] || 'default'} />
                </TableCell>
                <TableCell>{m.retryCount > 0 ? `${m.retryCount}/5` : '—'}</TableCell>
                <TableCell>
                  <Tooltip title="View raw message">
                    <IconButton size="small" onClick={() => openDetail(m)}><ViewIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  {m.status === 'failed' && m.retryable && (
                    <Tooltip title="Retry now">
                      <IconButton size="small" color="warning" onClick={() => handleRetry(m)}><RetryIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>

      <Dialog
        open={!!detail}
        onClose={() => setDetail(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '760px', md: '820px' },
            maxWidth: '850px !important',
            maxHeight: '88vh',
            borderRadius: '14px',
            bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
            backgroundImage: 'none',
            color: isDark ? '#F1F5F9' : '#0F172A',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
            fontSize: '1.05rem',
            fontWeight: 700
          }}
        >
          Raw HL7 Message
          <IconButton size="small" onClick={() => setDetail(null)} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.5, overflowY: 'auto' }}>
          {detail && (
            <>
              <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : 'text.secondary', mb: 1 }}>
                {detail.direction} · {detail.messageType} · {new Date(detail.createdAt).toLocaleString()}
                {detail.errorMessage && <> · <span style={{ color: '#ef4444' }}>{detail.errorMessage}</span></>}
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  maxHeight: '48vh',
                  overflowY: 'auto',
                  bgcolor: isDark ? '#121212' : 'grey.50',
                  borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
                  borderRadius: '8px'
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem', color: isDark ? '#E2E8F0' : 'inherit' }}>
                  {(detail.raw || '').split('\r').filter(Boolean).join('\n')}
                </pre>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.25,
            borderTop: 1,
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <Button onClick={() => setDetail(null)} variant="contained" sx={{ px: 2.5 }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
